// scoped to metrics-per-endpoint for now

import * as v from 'valibot'

// User-tunable inputs behind the Litmus cost-of-delay and fuel-cost figures. Stored as fractions/rates
// (not percentages) so they map directly onto the cost-of-delay formula and can be sent as query params
// without a translation layer. Single source of truth for client-frontend/models/users.ts and
// server/api/v2/entities/edge-groups/cost-of-delay.util.ts.
export interface ILitmusCostParameters {
	commercial_vehicle_proportion: number
	passenger_occupancy: number
	vot_passenger_dollars_per_hour: number
	vot_commercial_dollars_per_hour: number
	fuel_price_dollars_per_gallon: number
	days_per_year: number
}

// 2025 UMR Appendix C (2024 data): $24.01/person-hr passenger, $80.16/vehicle-hr commercial (already
// occupancy-weighted at 1.14 persons/truck), 1.5 persons/vehicle passenger occupancy, at a 95.4% / 4.6%
// passenger/commercial mix, plus a total_fuel_consumption cost term priced at 2024 EIA national annual averages
// ($3.304 regular, $3.760 diesel) blended by that mix — see docs/edge-group-cost-of-delay.md.
export const DEFAULT_LITMUS_COST_PARAMETERS: ILitmusCostParameters = {
	commercial_vehicle_proportion: 0.046,
	passenger_occupancy: 1.5,
	vot_passenger_dollars_per_hour: 24.01,
	vot_commercial_dollars_per_hour: 80.16,
	fuel_price_dollars_per_gallon: 3.32,
	days_per_year: 365.26,
}

// Pre-UMR-switch defaults (TxDOT, March 2014, per vehicle-hour) — litmus-network SQL parity.
// Kept for reference / a possible opt-back-in; not wired into resolveCostOfDelayParams.
export const LEGACY_TXDOT_COST_PARAMETERS: ILitmusCostParameters = {
	commercial_vehicle_proportion: 0.046,
	passenger_occupancy: 1,
	vot_passenger_dollars_per_hour: 21.73,
	vot_commercial_dollars_per_hour: 31.71,
	fuel_price_dollars_per_gallon: 0,
	days_per_year: 365.26,
}

// ---- Litmus bootstrap significance (report-litmus-v2 confidence column) ----

// One bootstrap test's outcome: 5000 mulberry32-seeded resamples of the before/after
// per-day series. diff = mean_2 - mean_1; lo/hi = 95% percentile CI of the change;
// p = +1-smoothed two-sided significance (p < 0.05 <=> the 95% CI excludes 0).
export const LitmusSignificanceResultSchema = v.object({
	diff: v.number(),
	lo: v.number(),
	hi: v.number(),
	p: v.number(),
	mean_1: v.number(),
	mean_2: v.number(),
})
export type ILitmusSignificanceResult = v.InferOutput<typeof LitmusSignificanceResultSchema>

// Bootstrap results keyed by metric field name, per entity scope. signals/phases are
// additionally keyed by the (ID-minimized) signal_id / phase_id. Fields with no result
// (missing daily series, < 2 days on a side) are simply absent from the maps.
export const LitmusSignificanceResultsSchema = v.object({
	network: v.record(v.string(), LitmusSignificanceResultSchema),
	signals: v.record(v.string(), v.record(v.string(), LitmusSignificanceResultSchema)),
	phases: v.record(v.string(), v.record(v.string(), LitmusSignificanceResultSchema)),
})
export type ILitmusSignificanceResults = v.InferOutput<typeof LitmusSignificanceResultsSchema>
